import { Box, Button, darken, lighten, styled, Tooltip, Typography } from '@mui/material'
import * as React from 'react'
import { HBox, VBox } from '../elements/basic-elements'

import { AutoAwesome, AutoAwesomeOutlined } from '@mui/icons-material'
import { colors } from '../constants/colors'
import { excludeScProps } from '../utilities/browser'

export type ChipProps = {
  title: string
  active: boolean
  emphasize?: boolean
}

type ChipListType = {
  title?: string
  onToggle: (chip: ChipProps) => void
  items: ChipProps[]
  center?: boolean
  gap?: number
  flat?: boolean
  emphasisText?: string
  overscrollPadding?: string
  renderChip?: (props: { title: string; active: boolean; emphasize?: boolean }) => React.ReactNode
  compact?: boolean
}
const ChipList: React.FC<ChipListType> = ({
  title,
  items,
  onToggle,
  center,
  flat,
  gap = 0.5,
  emphasisText,
  overscrollPadding = '0px',
  renderChip,
  compact = true
}) => {
  return (
    <VBox>
      {title && (
        <Typography variant="overline" color="textPrimary" sx={{ color: colors.BLACK_50 }}>
          {title}
        </Typography>
      )}
      <ChipContainer
        flexWrap={flat ? 'nowrap' : 'wrap'}
        gap={gap}
        alignItems="center"
        justifyContent={center ? 'center' : 'flex-start'}
        sx={
          flat && {
            overflow: 'scroll',
            ml: `calc(-1 * ${overscrollPadding})`,
            width: `calc(100% + (${overscrollPadding} + ${overscrollPadding}))`
          }
        }
      >
        {items.map(({ title, active, emphasize }, i) => {
          const padFirstItem = i == 0 && flat
          const padLastItem = i == items.length - 1 && flat
          return (
            <Box
              key={`chip-${i}`}
              sx={{
                pr: padLastItem ? overscrollPadding : 0,
                pl: padFirstItem ? overscrollPadding : 0
              }}
            >
              {renderChip ? (
                renderChip({ title, active, emphasize })
              ) : (
                <Chip
                  active={active}
                  onClick={() => onToggle({ title, active: !active })}
                  compact={compact}
                  emphasize={emphasize}
                >
                  <HBox gap={1} alignItems="center">
                    <Typography
                      variant={compact ? 'body2' : 'h6'}
                      fontSize={16}
                      fontWeight={400}
                      sx={flat && { whiteSpace: 'nowrap' }}
                    >
                      {title}
                    </Typography>
                    {emphasize && emphasisText && (
                      <Tooltip title={emphasisText} placement="top">
                        {active ? <AutoAwesome /> : <AutoAwesomeOutlined color="secondary" />}
                      </Tooltip>
                    )}
                  </HBox>
                </Chip>
              )}
            </Box>
          )
        })}
      </ChipContainer>
    </VBox>
  )
}

const ChipContainer = styled(HBox)<{ flat?: boolean }>`
  scroll-behavior: smooth;
`

export const Chip = styled(
  Button,
  excludeScProps(['active', 'emphasize', 'compact'])
)<{ active: boolean; compact?: boolean; emphasize?: boolean }>(({
  theme,
  active,
  emphasize,
  compact = true
}) => {
  const isDarkMode = theme.palette.mode === 'dark'
  const baseBackground = isDarkMode
    ? active
      ? colors.DARK_MODE_50
      : colors.DARK_MODE_70
    : active
      ? colors.GEORGIAN_BAY_70
      : theme.palette.tertiary.main

  const paddingY = theme.spacing(compact ? 1 : 1.5)
  const paddingX = theme.spacing(compact ? 2 : 2.5)

  return {
    borderRadius: '100px',
    padding: `${paddingY} ${paddingX} ${paddingY} ${paddingX}`,
    background: baseBackground,
    color: isDarkMode
      ? active
        ? colors.WHITE
        : colors.DARK_MODE_10
      : active
        ? colors.WHITE
        : colors.BLACK_100,
    transition: 'background 0.2s ease-in-out',

    '&:hover': {
      background: isDarkMode
        ? lighten(baseBackground, 0.05)
        : darken(baseBackground, active ? 0.1 : 0.02)
    },
    ...(emphasize
      ? {
          border: '1px solid',
          borderColor:
            theme.palette.mode == 'dark' ? colors.GEORGIAN_BAY_10 : colors.GEORGIAN_BAY_30
        }
      : {}),
    [theme.breakpoints.down('sm')]: {
      padding: `${theme.spacing(compact ? 1 : 1.5)} ${theme.spacing(2)}`
    }
  }
})

export default ChipList
